import { useEffect, useState } from 'react'
import { useAuth } from '../context/AuthContext'
import { Button } from './ui/Button'

function formatRemaining(ms: number): string {
  if (ms <= 0) return 'expired'
  const totalSec = Math.ceil(ms / 1000)
  const min = Math.floor(totalSec / 60)
  const sec = totalSec % 60
  if (min >= 60) {
    const h = Math.floor(min / 60)
    const m = min % 60
    return `${h}h ${m}m left`
  }
  if (min > 0) return `${min}m ${sec}s left`
  return `${sec}s left`
}

/** Fixed banner for platform support sessions (read-only or full, auto-expiring). */
export function SupportAccessBanner() {
  const { user, logout, isSupportSession } = useAuth()
  const session = user?.supportSession
  const [remainingMs, setRemainingMs] = useState(() =>
    session ? Math.max(0, session.expiresAtMs - Date.now()) : 0,
  )

  useEffect(() => {
    if (!session) return
    const tick = () => {
      const next = Math.max(0, session.expiresAtMs - Date.now())
      setRemainingMs(next)
      if (next <= 0) {
        void logout()
      }
    }
    tick()
    const id = window.setInterval(tick, 1000)
    return () => window.clearInterval(id)
  }, [session, logout])

  if (!isSupportSession || !session) return null

  const readOnly = session.accessMode === 'read_only'

  return (
    <div
      className="fixed left-0 right-0 top-0 z-[100] border-b border-amber-300/80 bg-amber-50/95 px-4 py-2 text-sm text-amber-950 shadow-md backdrop-blur-sm dark:border-amber-500/40 dark:bg-amber-950/90 dark:text-amber-50"
      role="status"
      aria-live="polite"
    >
      <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-3">
        <div>
          <span className="font-semibold">Support session</span>
          <span className="mx-2 text-amber-800/70 dark:text-amber-200/70">·</span>
          <span>{session.organisationName}</span>
          <span className="mx-2 text-amber-800/70 dark:text-amber-200/70">·</span>
          <span>{readOnly ? 'Read-only' : 'Full access'}</span>
          <span className="mx-2 text-amber-800/70 dark:text-amber-200/70">·</span>
          <span>{formatRemaining(remainingMs)}</span>
        </div>
        <Button type="button" size="sm" variant="secondary" onClick={() => void logout()}>
          Exit support session
        </Button>
      </div>
    </div>
  )
}
